<script lang="ts">
	import { tick } from 'svelte';
	import { page } from '$app/state';
	import { goto } from '$app/navigation';
	import PencilIcon from '@lucide/svelte/icons/pencil';
	import Trash2Icon from '@lucide/svelte/icons/trash-2';
	import {
		getSessions,
		getRelativeTimeTicker,
		subscribeRelativeTicker,
		markRenamed,
		markTrashed,
		markRestored,
		upsertSession,
		type SessionActivity,
		type SessionRow,
	} from '$lib/sessions-store.svelte';
	import { renameSession, trashSession, restoreSession } from '$lib/api';
	import { isScheduledUnread, markScheduledSessionSeen } from '$lib/scheduled-store.svelte';
	import { toast } from 'svelte-sonner';

	type Props = {
		activeId?: string;
		/** Client-side title filter from the sidebar's inline search input.
		 *  Empty string = show all. Matches against session.name only (the
		 *  global ⌘K modal handles full-text message search). */
		filter?: string;
	};

	let { activeId, filter = '' }: Props = $props();

	const sessions = getSessions();

	$effect(() => {
		const off = subscribeRelativeTicker();
		return off;
	});

	// Opening a session clears its green "new scheduled run" dot. Recorded for the
	// active session regardless of whether it's a known scheduled run yet, so it
	// still wins if the user opens a fresh run before the next schedule poll.
	$effect(() => {
		if (activeId) markScheduledSessionSeen(activeId);
	});

	const ACTIVITY_DOT_CLASSES: Record<SessionActivity, string> = {
		running: 'bg-green-500 ring-2 ring-green-500/25 motion-safe:animate-pulse',
		idle: 'bg-[color:var(--fg-faint)] opacity-70',
		errored: 'bg-red-500 ring-2 ring-red-500/25',
	};

	let editingId = $state<string | null>(null);
	let draftName = $state('');
	let busyId = $state<string | null>(null);
	let editInput: HTMLInputElement | undefined = $state();

	function startRename(row: SessionRow, event?: Event): void {
		event?.preventDefault();
		event?.stopPropagation();
		editingId = row.sessionId;
		draftName = row.name ?? '';
		tick().then(() => {
			editInput?.focus();
			editInput?.select();
		});
	}

	function cancelRename(): void {
		editingId = null;
		draftName = '';
	}

	async function commitRename(row: SessionRow): Promise<void> {
		// Re-entrancy guard. Pressing Enter sets busyId, which disables the input,
		// which BLURS it → onblur fires commitRename a second time while the first
		// PATCH is still in flight (editingId hasn't been cleared yet). Bailing when
		// this row is already busy — or no longer being edited — keeps it to ONE
		// /name call instead of two.
		if (editingId !== row.sessionId || busyId === row.sessionId) return;
		const next = draftName.trim();
		if (!next || next === row.name) {
			cancelRename();
			return;
		}
		busyId = row.sessionId;
		try {
			const res = await renameSession(row.sessionId, next);
			upsertSession({ sessionId: row.sessionId, name: res.name });
			markRenamed(row.sessionId);
		} catch (err) {
			console.warn('[SessionList] rename failed', err);
			toast.error('Rename failed', {
				description: err instanceof Error ? err.message : 'Server did not accept the new name.',
			});
		} finally {
			busyId = null;
			cancelRename();
		}
	}

	function onRenameKeydown(event: KeyboardEvent, row: SessionRow): void {
		if (event.key === 'Enter') {
			event.preventDefault();
			void commitRename(row);
		} else if (event.key === 'Escape') {
			event.preventDefault();
			cancelRename();
		}
	}

	// ── Move to Trash (SAY-1463) ───────────────────────────────────────────────
	// Trashing is reversible (the chat is hidden but kept intact until permanently
	// deleted from the Trash), so the row action fires immediately with no modal —
	// the toast offers a one-click Undo (restore). Permanent deletion lives in the
	// Trash list. Optimistic: mark trashed locally first so the row vanishes at
	// once; on server failure we restore the row and surface the error.
	async function trashOne(row: SessionRow, event?: Event): Promise<void> {
		event?.preventDefault();
		event?.stopPropagation();
		markTrashed(row.sessionId);
		// If the open chat was the one trashed, leave its route.
		if (page.params.sessionId === row.sessionId) void goto('/');
		try {
			await trashSession(row.sessionId);
			toast.success('Moved to Trash', {
				description: row.name ?? row.sessionId.slice(0, 8),
				action: {
					label: 'Undo',
					onClick: () => {
						markRestored(row.sessionId);
						void restoreSession(row.sessionId).catch((err) => {
							markTrashed(row.sessionId);
							toast.error('Undo failed', {
								description: err instanceof Error ? err.message : String(err),
							});
						});
					},
				},
			});
		} catch (err) {
			markRestored(row.sessionId);
			toast.error('Could not move to Trash', {
				description: err instanceof Error ? err.message : String(err),
			});
		}
	}

	function formatRelative(ts: number, _tick: number): string {
		const now = Date.now();
		const diffMs = now - ts;
		const diffMin = Math.floor(diffMs / 60_000);
		if (diffMin < 1) return 'just now';
		if (diffMin < 60) return `${diffMin}m ago`;
		const diffHour = Math.floor(diffMin / 60);
		if (diffHour < 24) return `${diffHour}h ago`;
		const d = new Date(ts);
		const yesterday = new Date(now);
		yesterday.setDate(yesterday.getDate() - 1);
		const isYesterday =
			d.getFullYear() === yesterday.getFullYear() &&
			d.getMonth() === yesterday.getMonth() &&
			d.getDate() === yesterday.getDate();
		if (isYesterday) return 'yesterday';
		const diffDays = Math.floor(diffMs / 86_400_000);
		if (diffDays < 7) return d.toLocaleDateString([], { weekday: 'short' });
		return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
	}

	type Bucket = 'Today' | 'Yesterday' | 'Earlier this week' | 'Earlier';
	const BUCKET_ORDER: Bucket[] = ['Today', 'Yesterday', 'Earlier this week', 'Earlier'];

	function bucketFor(ts: number): Bucket {
		const now = new Date();
		const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
		if (ts >= today) return 'Today';
		if (ts >= today - 86_400_000) return 'Yesterday';
		if (ts >= today - 7 * 86_400_000) return 'Earlier this week';
		return 'Earlier';
	}

	const groups = $derived.by(() => {
		// Re-read the ticker so relative-time updates + filter changes both
		// trigger re-grouping. Filter is case-insensitive substring on name.
		void getRelativeTimeTicker();
		const q = filter.trim().toLowerCase();
		const map: Record<Bucket, SessionRow[]> = {
			'Today': [],
			'Yesterday': [],
			'Earlier this week': [],
			'Earlier': [],
		};
		for (const s of sessions) {
			// Trashed chats are hidden here — they live in the Trash list.
			if (s.deletedAt) continue;
			if (q) {
				const name = (s.name ?? s.sessionId).toLowerCase();
				if (!name.includes(q)) continue;
			}
			map[bucketFor(s.lastActivityAt)].push(s);
		}
		return BUCKET_ORDER
			.map((b) => ({ bucket: b, rows: map[b] }))
			.filter((g) => g.rows.length > 0);
	});
</script>

{#if groups.length > 0}
	{#each groups as g (g.bucket)}
		<div class="mt-2 px-3.5 pt-1 pb-1 text-[10.5px] font-medium uppercase tracking-wider text-[color:var(--fg-faint)]">
			{g.bucket}
		</div>
		<ul class="flex flex-col gap-px px-2">
			{#each g.rows as s, i (s.sessionId)}
				{@const isActive = s.sessionId === activeId}
				{@const isEditing = editingId === s.sessionId}
				{@const relative = formatRelative(s.lastActivityAt, getRelativeTimeTicker())}
				<!-- anim-rise-in + --i: rows cascade in on first paint (keyed by
				     sessionId, so a re-derive from the relative-time ticker never
				     re-animates existing rows — only genuinely new ones). -->
				<li class="group relative overflow-hidden anim-rise-in" style="--i: {i}">
					{#if isEditing}
						<div class="flex items-center gap-1 rounded-md px-2.5 py-[7px]">
							<input
								bind:this={editInput}
								bind:value={draftName}
								onkeydown={(e) => onRenameKeydown(e, s)}
								onblur={() => void commitRename(s)}
								class="flex-1 bg-transparent text-base outline-none sm:text-[12.5px]"
								aria-label="Rename session"
								disabled={busyId === s.sessionId}
							/>
						</div>
					{:else}
						<a
							href={'/s/' + s.sessionId}
							aria-current={isActive ? 'page' : undefined}
							ondblclick={(e) => startRename(s, e)}
							class="flex cursor-pointer flex-col gap-[1px] rounded-md px-2.5 py-[7px] pr-12 transition-all duration-200 ease-[var(--ease-spring)] hover:bg-[color:var(--surface-2,var(--secondary))] motion-safe:hover:translate-x-px aria-[current=page]:bg-sidebar-accent aria-[current=page]:text-sidebar-accent-foreground aria-[current=page]:shadow-[inset_3px_0_0_0_var(--sidebar-primary)] focus-visible:bg-[color:var(--secondary)] focus-visible:outline-none"
						>
							<div class="flex items-center gap-2">
								{#if s.activity === 'running' || s.activity === 'errored'}
									<span
										class="inline-block size-1.5 shrink-0 rounded-full {ACTIVITY_DOT_CLASSES[s.activity]}"
										data-testid="activity-dot"
										data-state={s.activity}
										aria-label={s.activity}
									></span>
								{:else if isScheduledUnread(s.sessionId)}
									<!-- A timer-fired scheduled run produced this chat and it hasn't been
									     opened yet. A STATIC green dot — deliberately distinct from the
									     pulsing green "running" dot above (movement vs steady). Clears
									     when the user opens the chat (markScheduledSessionSeen). -->
									<span
										class="inline-block size-1.5 shrink-0 rounded-full bg-emerald-500 ring-2 ring-emerald-500/30"
										data-testid="scheduled-unread-dot"
										aria-label="new scheduled run"
										title="New scheduled run — not yet opened"
									></span>
								{/if}
								<span class="truncate text-[12.5px] font-medium">{s.name ?? s.sessionId.slice(0, 8)}</span>
							</div>
							<div class="truncate text-[10.5px] text-[color:var(--fg-faint)]">
								{relative}
							</div>
						</a>
						<div
							class="absolute right-1 top-1 flex gap-0.5 opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100"
						>
							<button
								type="button"
								class="rounded p-3 text-muted-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground sm:p-1"
								aria-label="Rename session"
								title="Rename"
								onclick={(e) => startRename(s, e)}
							>
								<PencilIcon class="size-3.5" />
							</button>
							<button
								type="button"
								class="rounded p-3 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive sm:p-1"
								aria-label="Move to Trash"
								title="Move to Trash"
								onclick={(e) => void trashOne(s, e)}
							>
								<Trash2Icon class="size-3.5" />
							</button>
						</div>
					{/if}
				</li>
			{/each}
		</ul>
	{/each}
{:else if filter.trim()}
	<div class="px-3.5 py-1 text-[11.5px] text-muted-foreground">No matches</div>
{:else}
	<div class="px-3.5 py-1 text-[11.5px] text-muted-foreground">No sessions yet</div>
{/if}
